import React, { useState } from "react";
import Total from "shared/components/Total";

import {
  Table,
  Typography,
  Skeleton,
  Input,
  Spin,
  Row
} from "antd";

import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { ColumnsType,  SorterResult } from "antd/es/table/interface";
import { usePermissions } from "hooks/usePermissions";
import PermissionWrapper from "shared/components/PermissionWrapper";
import {
  DELETE_HELIX_TEMPLATES_PERMISSION,
  EDIT_HELIX_TEMPLATES_PERMISSION,
  EditingState,
  HelixTemplate,
  TableHandlers,
} from "./Helix";

type Props = {
  values: {
    helixTemplates: HelixTemplate[];
    sortedInfo: SorterResult<HelixTemplate>;
    editing: EditingState;
    total: number;
  };
  
  handlers: TableHandlers;  
};

const HelixTemplatesTable: React.FC<Props> = ({ values, handlers }) => {
  const { helixTemplates, sortedInfo, editing, total } = values;
  const { handleChangeTable, handleClickEdit, handleClickDelete } = handlers;

  const { Link } = Typography;
  const { Search } = Input;

  const [searchValue, setSearchValue] = useState("");

  const { hasPermissions } = usePermissions();
  const canEditHelixTemplate = hasPermissions([EDIT_HELIX_TEMPLATES_PERMISSION]);
  const canDeleteHelixTemplate = hasPermissions([DELETE_HELIX_TEMPLATES_PERMISSION]);

  const data: HelixTemplate[] =
    helixTemplates?.map((item, index) => ({
      key: index.toString(),
      id: item.id,
      helixId: item.helixId,
      name: item.name,
      description: item.description,
    })) ?? [];

  const columns: ColumnsType<HelixTemplate> = [
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => {
        const aName = a.name ?? "";
        const bName = b.name ?? "";
        return aName.localeCompare(bName);
      },
      sortOrder: sortedInfo.field === "name" ? sortedInfo.order : undefined,
      render: (text, record) =>
        editing.isEditing && record.name !== editing.editingRow?.name ? (
          <Skeleton active  title paragraph={false} />
        ) : (
          text
        ),
    },
    {
      title: "Helix Id",
      dataIndex: "helixId",
      sorter: (a, b) => Number(a.helixId) - Number(b.helixId),
      sortOrder: sortedInfo.field === "helixId" ? sortedInfo.order : undefined,
      render: (text, record) =>
        editing.isEditing && record.name !== editing.editingRow?.name ? (
          <Skeleton active  title paragraph={false} />
        ) : (
          text
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text, record) =>
        editing.isEditing && record.name !== editing.editingRow?.name ? (
          <Skeleton active  title paragraph={false} />
        ) : (
          text
        ),
    },
    {
      title: "Edit",
      dataIndex: "edit",
      width: 100,
      render: (_text, record) =>
        editing.isEditing && record.name !== editing.editingRow?.name ? (
          <Skeleton active  title paragraph={false} />
        ) : (
          <PermissionWrapper
            hasPermissions={canEditHelixTemplate}
            tooltip={!canEditHelixTemplate ? "No permission to edit a Helix template." : ""}
          >
            <Link
              onClick={() => handleClickEdit(record)}
              disabled={!canEditHelixTemplate}
              style={{ marginRight: "10px" }}
            >
              <EditOutlined /> Edit
            </Link>
          </PermissionWrapper>
        ),
    },
    {
      title: "Delete",
      dataIndex: "delete",
      width: 100,
      render: (_text, record) =>
        editing.isDeleting && record.name !== editing.editingRow?.name ? (
          <Skeleton active  title paragraph={false} />
        ) : (
          <PermissionWrapper
            hasPermissions={canDeleteHelixTemplate}
            tooltip={!canDeleteHelixTemplate ? "No permission to delete a Helix template." : ""}
          >
            <Link
              onClick={() => handleClickDelete(record)}
              disabled={!canDeleteHelixTemplate}
              style={{ marginRight: "10px" }}
            >
              <DeleteOutlined /> Delete
            </Link>
          </PermissionWrapper>
        ),
    },
  ];

  const filteredData = searchValue
    ? data.filter((item) =>
        item.name?.toLowerCase().includes(searchValue.toLowerCase())
      )
    : data;

  return (
    <>
      <Search
        style={{ width: "300px", margin: "0 0 10px 0" }}
        placeholder="Search by name"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onSearch={(value) => setSearchValue(value)}
        allowClear
        enterButton
      />
      <Row className="title-wrap">
        <span style={{ margin: "5px 0 0" }}>
          {(editing.isEditing || editing.isDeleting) && (
            <Spin size="small" style={{ marginLeft: "10px" }} />
          )}
        </span>
        <Total number={total} text="Helix Templates" />
      </Row>
      <Table
        columns={columns}
        dataSource={filteredData}
        size="small"
        className="manage-page-table"
        style={{ width: "100%" }}
        onChange={handleChangeTable}
        pagination={{
          position: ["bottomCenter"],
          defaultPageSize: 20,
        }}
      />
    </>
  );
};

export default HelixTemplatesTable;
